import * as ngCdkScrolling from '@angular/cdk/scrolling'
import * as ngCore from '@angular/core'

import * as directives from '../../../../../directives'
import * as illustrations from '../../../../illustrations'
import * as overlay from '../../../../overlay'
import type * as models from '../../models'
import { AtnDsSelectSingleStore } from '../store'

/** Space reserved between the fitted overlay and the viewport edge. */
const OVERLAY_VIEWPORT_GUTTER = 16

/**
 * Overlay content for {@link atn-ds-select-single}.
 *
 * It owns the dropdown-only presentation concerns such as the visible item
 * window and any width calculation that depends on the rendered trigger.
 */
@ngCore.Component({
  encapsulation: ngCore.ViewEncapsulation.None,
  imports: [
    directives.ellipsisTooltip.Directive,
    ngCdkScrolling.ScrollingModule,
    overlay.attached.Component,
    illustrations.emptyState.Component,
  ],
  selector: 'atn-ds-single-overlay',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * __NOTE:__ this must match the actual height, in pixels, of the search element
   */
  public readonly searchHeight = 44
  /**
   * __NOTE:__ this must match the actual padding, in pixels, of the container element
   */
  public readonly containerPadding = 20
  /**
   * Height of a single item in the dropdown, in pixels
   */
  public readonly itemHeight = 26
  /**
   * Minimum number of items to render in the dropdown at a time
   */
  public readonly minItems = 1
  /**
   * Maximum number of items to render in the dropdown at a time
   */
  public readonly maxItems = 20
  /**
   * Horizontal padding applied across overlay items, in pixels.
   *
   * This is used when fitting the overlay width to the widest item label.
   */
  public readonly itemHorizontalPadding = 20

  /** @see {@link HTMLElement}   */
  public readonly originSg = ngCore.input.required<HTMLElement>({
    alias: 'origin',
  })

  /** @see {@link AtnDsSelectSingleStore} */
  public readonly store = ngCore.inject(AtnDsSelectSingleStore)
  /** Current rendered trigger width in pixels. */
  private readonly _originWidthPxSg = ngCore.signal(0)
  /** Current rendered trigger left position in viewport pixels. */
  private readonly _originLeftPxSg = ngCore.signal(0)
  /** Current viewport width in pixels for overlay clamping. */
  private readonly _viewportWidthPxSg = ngCore.signal(0)

  /**
   * Widest item label across the currently available items.
   *
   * This remains a reactive helper because item data can arrive after the
   * overlay opens, while the final width is derived from the latest measured
   * trigger geometry.
   */
  public readonly widestItemLabelWidthPxSg = ngCore.computed(
    (items = this.store.itemsSg(), origin = this.originSg()) =>
      this._getWidestItemLabelWidth(items, origin),
  )

  /** TODO:  */
  public readonly virtualScrollHeightSg = ngCore.computed(() => {
    // TODO: this seems to be a copy of what is in
    // select-multiple-overlay.component.ts (if so, we should abstract
    // somewhere)

    // LIST_ITEM_ELEMENT_HEIGHT is for "Select All" element
    const overlayMaxHeight = this.store.overlayMaxHeightSg()
    const calcHeight =
      overlayMaxHeight === undefined
        ? 0
        : overlayMaxHeight -
          this.searchHeight -
          this.itemHeight -
          this.containerPadding
    const availableHeight =
      calcHeight > this.itemHeight ? calcHeight : this.itemHeight
    // LIST_ELEMENT_BOTTOM_MARGIN is because last item has no margin bottom
    const calcItemsHeight =
      this.store.filteredItemsSg()?.length * this.itemHeight
    return availableHeight <= calcItemsHeight
      ? availableHeight
      : calcItemsHeight
  })

  /**
   * Resolves the overlay width from the current rendered trigger geometry.
   *
   * Resolution order:
   * 1. explicit pixel width from config
   * 2. widest item label plus existing padding when width is `fit-content`
   * 3. trigger width when width is omitted
   */
  public readonly overlayWidthPxSg = ngCore.computed(() => {
    const configuredWidth = this.store.overlayWidthSg()

    if (typeof configuredWidth === 'number') {
      return configuredWidth
    }

    const originWidth = this._originWidthPxSg()

    if (configuredWidth !== 'fit-content') {
      return originWidth
    }

    const desiredWidth = Math.ceil(
      this.widestItemLabelWidthPxSg() + this.itemHorizontalPadding,
    )

    return this._clampOverlayWidth({
      desiredWidth: Math.max(originWidth, desiredWidth),
      originLeft: this._originLeftPxSg(),
      viewportWidth: this._viewportWidthPxSg(),
    })
  })

  constructor() {
    ngCore.effect(onCleanup => {
      const origin = this.originSg()
      const measureOrigin = () => {
        this._measureOrigin(origin)
      }

      measureOrigin()

      const resizeObserver = new ResizeObserver(measureOrigin)
      resizeObserver.observe(origin)

      onCleanup(() => {
        resizeObserver.disconnect()
      })
    })

    ngCore.effect(onCleanup => {
      const origin = this.originSg()

      if (!this.store.isOpenSg()) {
        return undefined
      }

      const view = origin.ownerDocument.defaultView

      if (!view) {
        return undefined
      }

      const handleResize = () => {
        this._measureOrigin(origin)
      }

      handleResize()
      view.addEventListener('resize', handleResize)

      onCleanup(() => {
        view.removeEventListener('resize', handleResize)
      })
    })
  }

  /** TODO:  */
  public setSelectedHandler(item: models.Item): void {
    this.store.patchState({ isOpen: false, selectedValue: item.value })

    item.onSelect?.()
  }

  /**
   * Keeps fit-content widths inside the current viewport while preserving the
   * measured content width whenever there is enough space.
   */
  private _clampOverlayWidth({
    desiredWidth,
    originLeft,
    viewportWidth,
  }: {
    desiredWidth: number
    originLeft: number
    viewportWidth: number
  }): number {
    if (viewportWidth <= 0) {
      return desiredWidth
    }

    const availableWidth = viewportWidth - originLeft - OVERLAY_VIEWPORT_GUTTER

    return Math.min(desiredWidth, Math.max(0, availableWidth))
  }

  /**
   * Captures the current trigger geometry so overlay width can be derived from
   * signals instead of reading DOM measurements inside a computed.
   */
  private _measureOrigin(origin: HTMLElement): void {
    const rect = origin.getBoundingClientRect()
    const document = origin.ownerDocument

    this._originWidthPxSg.set(origin.offsetWidth)
    this._originLeftPxSg.set(rect.left)
    this._viewportWidthPxSg.set(
      document.defaultView?.innerWidth ?? document.documentElement.clientWidth,
    )
  }

  /**
   * Reuses the trigger typography so canvas text measurement matches the
   * overlay item labels.
   */
  private _getMeasurementFont(origin: HTMLElement): string {
    const labelEl =
      origin.querySelector<HTMLElement>('.atn-ds-single-select__label') ??
      origin
    const style = getComputedStyle(labelEl)

    return (
      style.font ||
      [style.fontStyle, style.fontVariant, style.fontWeight, style.fontSize]
        .filter(Boolean)
        .concat(style.fontFamily)
        .join(' ')
    )
  }

  /**
   * Measures the widest label across all available items so fit-content width
   * stays stable even when the overlay content is filtered.
   */
  private _getWidestItemLabelWidth(
    items: readonly models.Item[],
    origin: HTMLElement,
  ): number {
    if (items.length === 0) {
      return 0
    }

    const measurementCanvas = origin.ownerDocument.createElement('canvas')
    const context = measurementCanvas.getContext('2d')

    if (!context) {
      return items.reduce(
        (widestWidth, item) => Math.max(widestWidth, item.label.length * 8),
        0,
      )
    }

    context.font = this._getMeasurementFont(origin)

    return items.reduce(
      (widestWidth, item) =>
        Math.max(widestWidth, context.measureText(item.label).width),
      0,
    )
  }
}
